import { useState, type CSSProperties } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { api, errorMessage, sessionToken } from '../../api';
import { EmptyState, ErrorState, LineDot, Loading, useTitle } from '../../ui/bits';
import { Fig, PageHead, Ring, Stat } from '../../ui/viz';
import { count, peso } from '../../lib/format.js';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART, salesBarClass } from '../../lib/voice.js';
import { StaleNote } from '../payments/kit';
import { ShareRow } from '../payments/money';
import { PeriodPicker } from './PeriodPicker';
import { dayLabel, lastNDays, shortDay, type Period } from './period.js';

import { cancelRows, csvFileName, dailyBars, fetchReportCsv, lineRows, reportKey, shopRows, totalsView } from './lib.js';
import './reports.css';

/** Save a Blob as a file via a temporary object URL. */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * /ops/reports: a period (7 / 30 days or custom, up to 92), the totals as stat tiles, GMV per day
 * as bars (hover or focus a day for its figure), sales by line and by shop as share bars, why
 * orders were cancelled, and "Download CSV" (one row per day).
 */
export function OpsReportsPage() {
  useTitle('Reports');
  const toast = useToast();
  const [period, setPeriod] = useState<Period>(() => lastNDays(7));
  const q = useQuery({ queryKey: reportKey(period), queryFn: () => api.opsReport(period), placeholderData: keepPreviousData });
  const data = q.data;

  const download = useMutation({
    mutationFn: (p: Period) => fetchReportCsv(p, { token: sessionToken.get() }),
    onSuccess: (blob, p) => {
      saveBlob(blob, csvFileName(p));
      toast(`Saved ${csvFileName(p)}.`);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't download the CSV. Try again."), 'error'),
  });

  const tiles = data ? totalsView(data.totals) : [];
  const tile = (label: string) => tiles.find((t) => t.label === label) ?? { label, value: '—', detail: '' };
  const onTime = data?.totals.onTimePct ?? null;

  return (
    <div className="mn-page">
      <PageHead
        title="Reports"
        meta="All lines · Manila days"
        actions={
          <button type="button" className="btn btn-primary" disabled={download.isPending} aria-busy={download.isPending || undefined} onClick={() => download.mutate(period)}>
            <Icon name="arrow" />
            {download.isPending ? 'Preparing…' : 'Download CSV'}
          </button>
        }
      />

      <section className="card mn-bar dv-rise" aria-label="Period" style={rise(0)}>
        <PeriodPicker
          idPrefix="rp-period"
          label="Period"
          value={period}
          onChange={setPeriod}
          presets={[
            { id: '7', label: 'Last 7 days', period: lastNDays(7) },
            { id: '30', label: 'Last 30 days', period: lastNDays(30) },
          ]}
        />
      </section>

      {q.isError && !data ? (
        <section className="card">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </section>
      ) : !data ? (
        <Loading label="Loading the report" rows={4} />
      ) : (
        <div className="mn-page" aria-busy={q.isFetching || undefined}>
          <StaleNote show={q.isError} what="the report" />
          <section aria-label="Totals" className="mn-stats4 dv-rise" style={rise(1)}>
            <Stat label="GMV" tone="ink" value={tile('GMV').value} sub={tile('GMV').detail} visual={<MiniBars values={data.days.map((d) => d.gmvCentavos)} />} />
            <Stat label="Orders" value={tile('Orders').value} sub={data.totals.cancelled ? <span className="dv-chip bad">{tile('Orders').detail}</span> : tile('Orders').detail} />
            <Stat label="Average order" value={tile('Average order').value} sub="Delivered" />
            <Stat label="Commission" value={tile('Commission').value} sub="Partner shops" />
          </section>
          <section aria-label="More totals" className="mn-stats4 mn-quiet-stats dv-rise" style={rise(2)}>
            <Stat label="Delivery fees" value={tile('Delivery fees').value} sub={tile('Delivery fees').detail} />
            <Stat label="Refunds" value={tile('Refunds').value} sub={tile('Refunds').detail} />
            <Stat label="On time" value={tile('On time').value} sub={tile('On time').detail} visual={<Ring pct={onTime} tone={onTime === null ? 'good' : onTime >= 90 ? 'good' : onTime >= 75 ? 'warn' : 'bad'} />} />
            <Stat label="New customers" value={tile('New customers').value} sub="First order" />
          </section>

          {data.totals.orders === 0 ? (
            <section className="card">
              <EmptyState art={STATE_ART.noOrders} title="No orders in this period">
                Pick a longer period.
              </EmptyState>
            </section>
          ) : (
            <div className="mn-grid">
              <section className="card dv-card mn-8 mn-stretch dv-rise" aria-labelledby="rp-days-h" style={rise(3)}>
                <DayHead days={data.days} />
                <DailyBars days={data.days} />
              </section>

              <section className="card dv-card mn-4 mn-stretch dv-rise" aria-labelledby="rp-line-h" style={rise(4)}>
                <div className="dv-card-h">
                  <h2 id="rp-line-h">By line</h2>
                </div>
                {data.byLine.length ? (
                  <>
                    <MixStrip rows={lineRows(data.byLine)} />
                    <ul className="mn-shares">
                      {lineRows(data.byLine).map((r) => (
                        <ShareRow
                          key={r.line}
                          lead={<LineDot line={r.line} />}
                          name={r.name}
                          sub={`${r.orders} orders`}
                          pct={r.pct}
                          barClass={salesBarClass(r.line)}
                          label={`${r.name}: ${r.pct}% of GMV`}
                          value={
                            <>
                              <Fig text={r.gmv} size="xs" animate={false} />
                              <span className="mn-pc">{r.pct}%</span>
                            </>
                          }
                        />
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="dv-quiet">No sales yet.</div>
                )}
              </section>

              <section className="card dv-card mn-8 dv-rise" aria-labelledby="rp-shop-h" style={rise(5)}>
                <div className="dv-card-h">
                  <h2 id="rp-shop-h">By shop</h2>
                  {data.byShop.length ? <span className="dv-chip">{count(data.byShop.length)}</span> : null}
                </div>
                {data.byShop.length ? (
                  <ul className="mn-shares">
                    {shopRows(data.byShop).map((r) => (
                      <ShareRow
                        key={r.id}
                        name={r.name}
                        sub={`${r.orders} orders`}
                        pct={r.pct}
                        label={`${r.name}: ${r.pct}% of GMV`}
                        value={
                          <>
                            <Fig text={r.gmv} size="sm" animate={false} />
                            <span className="mn-pc">{r.pct}%</span>
                          </>
                        }
                      />
                    ))}
                  </ul>
                ) : (
                  <div className="dv-quiet">No shop sales yet.</div>
                )}
              </section>

              <section className="card dv-card mn-4 dv-rise" aria-labelledby="rp-cancel-h" style={rise(6)}>
                <div className="dv-card-h">
                  <h2 id="rp-cancel-h">Why orders were cancelled</h2>
                  {data.totals.cancelled ? <span className="dv-chip">{count(data.totals.cancelled)}</span> : null}
                </div>
                {data.cancelReasons.length ? (
                  <ul className="mn-shares">
                    {cancelRows(data.cancelReasons).map((r) => (
                      <ShareRow
                        key={r.reason}
                        name={r.reason}
                        pct={r.pct}
                        barClass="muted-bar"
                        label={`${r.reason}: ${r.pct}% of cancellations`}
                        value={
                          <>
                            <Fig text={r.count} size="xs" animate={false} />
                            <span className="mn-pc">{r.pct}%</span>
                          </>
                        }
                      />
                    ))}
                  </ul>
                ) : (
                  <div className="dv-quiet">
                    <Icon name="check" />
                    No cancellations. Nice.
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

type Days = Parameters<typeof dailyBars>[0];

/** The chart's header: its title, then the peak day and the daily average as figures. */
function DayHead({ days }: { days: Days }) {
  const peak = days.reduce<Days[number] | null>((m, d) => (m === null || d.gmvCentavos > m.gmvCentavos ? d : m), null);
  const avg = days.length ? Math.round(days.reduce((s, d) => s + d.gmvCentavos, 0) / days.length) : 0;
  return (
    <div className="dv-card-h rp-head">
      <h2 id="rp-days-h">GMV by day</h2>
      <span className="dv-chip">{count(days.length)} days</span>
      <span className="grow" />
      {peak && peak.gmvCentavos > 0 ? (
        <span className="rp-kv">
          <span className="dv-sub">Peak · {shortDay(peak.day)}</span>
          <Fig text={peso(peak.gmvCentavos)} size="sm" animate={false} />
        </span>
      ) : null}
      <span className="rp-kv">
        <span className="dv-sub">Daily avg</span>
        <Fig text={peso(avg)} size="sm" animate={false} />
      </span>
    </div>
  );
}

/** One column per day; hover or focus shows that day's GMV in a tip (drawn by CSS from data-*). */
function DailyBars({ days }: { days: Days }) {
  const bars = dailyBars(days);
  const n = bars.length;
  return (
    <div className={n > 14 ? 'rp-chart dense' : 'rp-chart'} role="list" aria-label="GMV by day">
      {bars.map((b, i) => (
        <div
          key={b.day}
          className={`rp-col${i < 2 ? ' first' : i >= n - 2 ? ' last' : ''}`}
          role="listitem"
          tabIndex={0}
          aria-label={b.title}
          data-day={dayLabel(b.day)}
          data-val={peso(days[i]?.gmvCentavos ?? 0)}
          style={{ ['--j' as string]: i } as CSSProperties}
        >
          <div className="rp-track">
            <i className={b.pct ? 'rp-fill' : 'rp-fill empty'} style={{ height: `${b.pct}%` }} />
          </div>
          <span className="rp-day" aria-hidden="true">
            {b.showLabel ? b.label : ' '}
          </span>
        </div>
      ))}
    </div>
  );
}

/** All lines in one bar, in line colours (the mix at a glance). */
function MixStrip({ rows }: { rows: ReturnType<typeof lineRows> }) {
  return (
    <span className="rp-mix" aria-hidden="true">
      {rows
        .filter((r) => r.pct > 0)
        .map((r) => (
          <i key={r.line} className={salesBarClass(r.line)} style={{ flexGrow: r.pct }} />
        ))}
    </span>
  );
}

/** Tiny day bars for the GMV tile (Ink tile: Cream bars, the last day in Pop Yellow). */
function MiniBars({ values }: { values: number[] }) {
  const v = values.slice(-14);
  if (v.length < 2) return null;
  const max = Math.max(1, ...v);
  return (
    <span className="dv-bars rp-mini" aria-hidden="true">
      {v.map((x, j) => (
        <i key={j} className={j === v.length - 1 ? 'hot' : ''} style={{ height: `${Math.max(7, (x / max) * 100)}%`, ['--j' as string]: j } as CSSProperties} />
      ))}
    </span>
  );
}
